import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { IconChevronRight } from "@tabler/icons-react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { MintLicense } from "../../components/stripe/MintLicense";
import { MintNFT } from "../../components/stripe/MintNFT";
import { auth, db } from "../../config/firebase";
import { StripeProductTypes } from "../../utils/enums/StripeProductTypes";
export const MintPage = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const sessionId = urlParams.get('sessionId');
  const publicAddress = urlParams.get('publicAddress');
  const nftUid = urlParams.get('nftUid');
  const productType = urlParams.get('productType');
  const licenseUid = urlParams.get('licenseUid');
  const licenseRequestUid = urlParams.get('licenseRequestUid');

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [authCheckAttempts, setAuthCheckAttempts] = useState(0);
  const [nftData, setNftData] = useState(null);
  const MAX_AUTH_ATTEMPTS = 6;

  // Auth state listener
  useEffect(() => {
    console.log("Setting up auth state listener");
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user?.email);
      if (user) {
        setIsAuthenticated(true);
        setAuthCheckAttempts(0);
        setIsLoading(true);
        console.log("User authenticated:", user.uid, user.email);
      } else {
        console.log("No user authenticated");
        setIsAuthenticated(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Aggressive auth checking with retries
  useEffect(() => {
    const checkAuthAggressively = async () => {
      try {
        if (auth.currentUser) {
          console.log("Found existing auth.currentUser:", auth.currentUser.email);
          setIsAuthenticated(true);
          setIsLoading(true);
          return;
        }

        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          console.log("Token refreshed for user:", auth.currentUser.email);
          setIsAuthenticated(true);
          setIsLoading(true);
        } else {
          console.log("No current user to refresh token");
          
          if (authCheckAttempts < MAX_AUTH_ATTEMPTS) {
            console.log(`Auth check attempt ${authCheckAttempts + 1}/${MAX_AUTH_ATTEMPTS}`);
            setAuthCheckAttempts(prev => prev + 1);
          }
        }
      } catch (error) {
        console.error("Error in auth check:", error);
        if (authCheckAttempts < MAX_AUTH_ATTEMPTS) {
          setAuthCheckAttempts(prev => prev + 1);
        }
      }
    };
    
    checkAuthAggressively();
  }, [authCheckAttempts]);

  // Retry mechanism for auth checks
  useEffect(() => {
    if (!isAuthenticated && authCheckAttempts > 0 && authCheckAttempts < MAX_AUTH_ATTEMPTS) {
      const retryDelay = Math.min(1000 * authCheckAttempts, 5000);
      console.log(`Retrying auth check in ${retryDelay}ms (attempt ${authCheckAttempts})`);
      
      const timer = setTimeout(() => {
        setAuthCheckAttempts(prev => prev + 1);
      }, retryDelay);

      return () => clearTimeout(timer);
    }
  }, [authCheckAttempts, isAuthenticated]);

  // Manual auth check on component mount and periodically
  useEffect(() => {
    const performManualAuthCheck = () => {
      console.log("Performing manual auth check");
      
      const hasCurrentUser = !!auth.currentUser;
      const hasAuthToken = !!localStorage.getItem('firebase:authUser:' + auth.app.options.apiKey + ':[DEFAULT]');
      
      console.log("Manual check - currentUser:", hasCurrentUser, "localStorage token:", hasAuthToken);
      
      if (hasCurrentUser && !isAuthenticated) {
        console.log("Found user but not authenticated, updating state");
        setIsAuthenticated(true);
        setIsLoading(true);
      }
    };

    performManualAuthCheck();

    const intervals = [];
    [500, 1000, 2000, 3000, 5000, 8000].forEach(delay => {
      intervals.push(setTimeout(performManualAuthCheck, delay));
    });

    return () => {
      intervals.forEach(clearTimeout);
    };
  }, [isAuthenticated]);

  // Fetch user data when authenticated
  useEffect(() => {
    console.log("isAuthenticated changed:", isAuthenticated);
    if (isAuthenticated) {
      const userCollectionsCollection = collection(db, "royalty_templates");
      console.log("uid", auth.currentUser.uid);
      console.log("email", auth.currentUser.email);
      let nftDataQuery = query(userCollectionsCollection, 
        where("userUid", "==", auth.currentUser.uid),
        where("uid", "==", nftUid)
      );
      
      console.log("Query params:", auth.currentUser.uid);
      
      const unsubscribe = onSnapshot(nftDataQuery, (querySnapshot) => {
        let newData = [];
        console.log("Total documents in snapshot:", querySnapshot.size);
        
        querySnapshot.forEach(doc => {
          const data = doc.data();
          newData.push(data);
        });
        

        const userData = getDoc(doc(db, "users", auth.currentUser.uid));

        console.log("Total records processed:", newData.length);
        setUserData(userData);
        setNftData(newData[0]);
        if (newData.length > 1) {
          throw new Error("Multiple NFTs found for the same UID");
        }
        console.log("User Data loaded:", newData);
        setIsLoading(false);
      });
      return () => unsubscribe();
    }
  }, [isAuthenticated, nftUid]);

  // Handle auth failure after max attempts - but only if we're still loading
  useEffect(() => {
    if (!isAuthenticated && authCheckAttempts >= MAX_AUTH_ATTEMPTS && isLoading) {
      console.log("Max auth attempts reached, showing payment success fallback");
      setIsLoading(false);
    }
  }, [authCheckAttempts, isAuthenticated, isLoading]);

  // Loading state - show loading until we have a definitive auth result
  if (isLoading || (authCheckAttempts < MAX_AUTH_ATTEMPTS && !isAuthenticated)) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          m: "auto",
          p: 5,
          borderRadius: 5,
          boxShadow: 5,
          bgcolor: "#FFF",
          maxWidth: "sm"
        }}
      >
        <CircularProgress />
        <Typography>Verifying your account...</Typography>
      </Stack>
    );
  }

  // Auth failed fallback - only show if we've exhausted attempts AND still not authenticated
  // if (!isAuthenticated && authCheckAttempts >= MAX_AUTH_ATTEMPTS && !isLoading) {
  //   return (
  //     <Stack
  //       direction="column"
  //       alignItems="center"
  //       spacing={2}
  //       sx={{
  //         m: "auto",
  //         p: 5,
  //         borderRadius: 5,
  //         boxShadow: 5,
  //         bgcolor: "#FFF",
  //         maxWidth: "sm"
  //       }}
  //     >
  //       <Typography variant="h3">
  //         Payment Succeeded!
  //       </Typography>
  //       <Typography sx={{ color: grey[600], textAlign: 'center' }}>
  //         Your payment was processed successfully. Go to your portfolio to finish minting your NFT.
  //       </Typography>
  //       <Button
  //         component={Link}
  //         to="/portfolio"
  //         className="sign-button"
  //         endIcon={<IconChevronRight />}
  //         sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
  //       >
  //         Go to Portfolio
  //       </Button>
  //     </Stack>
  //   );
  // }

  // If authenticated but no userData yet, keep loading
  if (isAuthenticated && !userData) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          m: "auto",
          p: 5,
          borderRadius: 5,
          boxShadow: 5,
          bgcolor: "#FFF",
          maxWidth: "sm"
        }}
      >
        <CircularProgress />
        <Typography>Loading your data...</Typography>
      </Stack>
    );
  }

  // Render NFT minting component
  if (productType === StripeProductTypes.NFT) {
    if (!userData || !nftData || !sessionId || !publicAddress || !nftUid) {
      console.log("-----------------------------------------------");
      console.log("ERROR: Missing required data for NFT minting");
      console.log("userData", userData);
      console.log("nftData", nftData);
      console.log("sessionId", sessionId);
      console.log("publicAddress", publicAddress);
      console.log("nftUid", nftUid);
      return (
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
          sx={{
            m: "auto",
            p: 5,
            borderRadius: 5,
            boxShadow: 5,
            bgcolor: "#FFF",
            maxWidth: "sm"
          }}
        >
          <Typography variant="h3">
            Payment completed
          </Typography>
          <Typography sx={{ color: grey[600], textAlign: 'center' }}>
            Please check your portfolio to finish minting your NFT.
          </Typography>
          <Button
            component={Link}
            to="/portfolio"
            className="sign-button"
            endIcon={<IconChevronRight />}
            sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
          >
            Go to Portfolio to mint NFT
          </Button>
        </Stack>
      );
    }

    return (
      <MintNFT 
        nftUid={nftUid} 
        publicAddress={publicAddress} 
        sessionId={sessionId}
        userData={userData}
        nftData={nftData}
      />
    );
  }

  // Render License minting component
  if (productType === StripeProductTypes.LICENSE) {
    console.log("MINTING LICENSE");
    if (!nftUid || !licenseUid || !publicAddress || !sessionId || !licenseRequestUid || !userData) {
      return (
        <Stack
          direction="column"
          alignItems="center"
          spacing={2}
          sx={{
            m: "auto",
            p: 5,
            borderRadius: 5,
            boxShadow: 5,
            bgcolor: "#FFF",
            maxWidth: "sm"
          }}
        >
          <Typography variant="h3">
            Payment completed
          </Typography>
          <Typography sx={{ color: grey[600], textAlign: 'center' }}>
            Please check your portfolio to finish minting your license.
          </Typography>
          <Button
            component={Link}
            to="/portfolio"
            className="sign-button"
            endIcon={<IconChevronRight />}
            sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
          >
            Go to Portfolio to mint License
          </Button>
        </Stack>
      );
    }
    console.log("RETURNING MINTING LICENSE");
    return (
      <MintLicense 
        nftUid={nftUid} 
        licenseUid={licenseUid} 
        publicAddress={publicAddress} 
        sessionId={sessionId} 
        licenseRequestUid={licenseRequestUid}
      />
    );
  }

  // Invalid product type
  return (
    <Stack
      direction="column"
      alignItems="center"
      spacing={2}
      sx={{
        m: "auto",
        p: 5,
        borderRadius: 5,
        boxShadow: 5,
        bgcolor: "#FFF",
        maxWidth: "sm"
      }}
    >
      <Typography variant="h3">
        Payment completed
      </Typography>
      <Typography sx={{ color: grey[600], textAlign: 'center' }}>
        Please check your portfolio to finish minting your NFT.
      </Typography>
      <Button
        component={Link}
        to="/portfolio"
        className="sign-button"
        endIcon={<IconChevronRight />}
        sx={{ textTransform: "none", px: 2, borderRadius: 3 }}
      >
        Go to Portfolio to mint NFT
      </Button>
    </Stack>
  );
};